import { useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/documents/$id';
import { ArrowLeft, Printer } from 'lucide-react';
import { useDocument } from '@/hooks/use-documents';
import { useEscapeKey } from '@/hooks/use-escape-key';
import { useNavigationGuard } from '@/hooks/use-navigation-guard';
import { UnsavedChangesDialog } from '@/components/custom/unsaved-changes-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DocumentWorkspace } from './DocumentWorkspace';
import { REVIEW_VARIANTS, STATUS_VARIANTS } from './constants';

export default function DocumentDetailPage(): React.ReactElement {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  // Header data only — the workspace below owns the full fetch + loading
  // state; we read the same query just for the title row badges. React
  // Query dedupes the underlying request.
  const { data: document } = useDocument(id);
  const [isDirty, setIsDirty] = useState(false);

  const handleBack = useCallback((): void => {
    navigate({ to: '/documents' });
  }, [navigate]);

  const guard = useNavigationGuard(isDirty, handleBack);
  useEscapeKey(guard.promptOrLeave);

  return (
    <div className="flex h-full flex-col">
      {/* Header — back + print are always reachable; the title row
       * shows a skeleton placeholder during the initial fetch so we
       * don't flash "Unknown Patient" before data lands. */}
      <div className="flex items-center gap-3 border-b px-4 py-3 shrink-0">
        <Button variant="ghost" size="icon" onClick={handleBack} aria-label="Go back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {document ? (
            <>
              <span className="font-medium truncate">
                {document.patientName ?? 'Unknown Patient'}
              </span>
              {document.authNumber && (
                <span className="text-sm text-muted-foreground">{document.authNumber}</span>
              )}
              {document.status && (
                <Badge
                  variant={STATUS_VARIANTS[document.status] ?? 'outline'}
                  className="capitalize"
                >
                  {document.status}
                </Badge>
              )}
              {document.reviewStatus && (
                <Badge
                  variant={REVIEW_VARIANTS[document.reviewStatus] ?? 'outline'}
                  className="capitalize"
                >
                  {document.reviewStatus}
                </Badge>
              )}
            </>
          ) : (
            <span aria-hidden="true" className="h-4 w-40 animate-pulse rounded bg-muted" />
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => window.print()}
            title="Print extraction"
            aria-label="Print extraction"
          >
            <Printer className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Workspace owns the split panes, the data fetch, and the print
       * portal. Same component is rendered inside each task's extraction
       * tab — keep them in sync via that shared layer rather than
       * editing two files. */}
      <div className="flex-1 min-h-0">
        <DocumentWorkspace documentId={id} onDirtyChange={setIsDirty} />
      </div>

      <UnsavedChangesDialog {...guard.dialogProps} />
    </div>
  );
}
